import {
  integer,
  pgTable,
  varchar,
  boolean,
  pgEnum,
  timestamp,
} from "drizzle-orm/pg-core";
import { createSelectSchema } from "drizzle-zod";
import { sql, relations } from "drizzle-orm";
import z from "zod";

export const adminsTable = pgTable("admins", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  email: varchar().notNull().unique(),
  password: varchar().notNull(),
});
export type AdminEntity = typeof adminsTable.$inferInsert & { id: number };
export const adminEntitySchema = createSelectSchema(adminsTable);

export const tagsTable = pgTable("tags", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: varchar().notNull().unique(),
});
export type TagEntity = typeof tagsTable.$inferInsert & { id: number };
export const tagEntitySchema = createSelectSchema(tagsTable);

export const daysTable = pgTable("days", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  date: varchar().notNull().unique(),
});
export type Day = typeof daysTable.$inferInsert & { id: number };
export const dayEntitySchema = createSelectSchema(daysTable);

export const roomsTable = pgTable("rooms", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: varchar().notNull().unique(),
  description: varchar(),
});
export type RoomEntity = typeof roomsTable.$inferInsert & { id: number };
export const roomEntitySchema = createSelectSchema(roomsTable);

export const speakersTable = pgTable("speakers", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  fullName: varchar().notNull().unique(),
});
export type SpeakerEntity = typeof speakersTable.$inferInsert & { id: number };
export const speakerEntitySchema = createSelectSchema(speakersTable);

export const lecturesTable = pgTable("lectures", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  title: varchar().notNull(),
  description: varchar(),
  startTime: varchar().notNull(),
  endTime: varchar().notNull(),
  dayDate: varchar()
    .notNull()
    .references(() => daysTable.date),
  roomId: integer(),
  sessionId: integer(),
  order: integer(),
});
export type LectureEntity = typeof lecturesTable.$inferInsert & { id: number };
export const lectureEntitySchema = createSelectSchema(lecturesTable);

export const sessionsTable = pgTable("sessions", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  title: varchar().notNull(),
  description: varchar(),
});
export type SessionEntity = typeof sessionsTable.$inferInsert & { id: number };
export const sessionEntitySchema = createSelectSchema(sessionsTable);

export const attendantsTable = pgTable("attendants", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  fullName: varchar().notNull(),
  pin: varchar().notNull(),
});
export type AttendantEntity = typeof attendantsTable.$inferInsert & {
  id: number;
};
export const attendantEntitySchema = createSelectSchema(attendantsTable);

export const companiesTable = pgTable("companies", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  standNumber: varchar().notNull(),
  title: varchar().notNull(),
  contactDetails: varchar().notNull(),
  pin: varchar().notNull(),
  youngDoctorsSupport: boolean(),
  description: varchar(),
  linkUrl: varchar(),
  logoUrl: varchar(),
  order: integer(),
});
export type Company = typeof companiesTable.$inferInsert & { id: number };
export const companySchema = createSelectSchema(companiesTable);

export const contestsTable = pgTable("contests", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: varchar().notNull(),
  description: varchar(),
  dueDate: varchar(),
  linkUrl: varchar(),
  companyId: integer()
    .notNull()
    .references(() => companiesTable.id, { onDelete: "cascade" }),
});
export type ContestEntity = typeof contestsTable.$inferInsert & { id: number };
export const contestEntitySchema = createSelectSchema(contestsTable);

export const participantPassTypeEnum = pgEnum("participantPassType", [
  "student",
  "doctor",
]);
export const participantPassStatusEnum = pgEnum("participantPassStatus", [
  "active",
  "blocked",
]);

export const participantPassesTable = pgTable("participantPasses", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  identifier: varchar().unique(),
  type: participantPassTypeEnum().default("student"),
  status: participantPassStatusEnum().default("active"),
  comments: varchar(),
});
export type ParticipantPass = typeof participantPassesTable.$inferInsert & {
  id: number;
};
export const participantPassSchema = createSelectSchema(participantPassesTable);

export const participantsTable = pgTable("participants", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  createdAt: timestamp("createdAt", { mode: "date" }).notNull().defaultNow(),
  updatedAt: timestamp("updatedAt", { mode: "date" })
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
  identifier: varchar().notNull(),
  firstName: varchar(),
  lastName: varchar(),
  email: varchar(),
  phone: varchar(),
  speciality: varchar(),
  school: varchar(),
  city: varchar(),
  workplace: varchar(),
  // todo: migrate to list of consents
  gdprConsent: boolean(),
  marketingConsent: boolean(),
  youngDoctorConsent: boolean(),
});
export type ParticipantEntity = typeof participantsTable.$inferInsert & {
  id: number;
};
export const participantEntitySchema = createSelectSchema(participantsTable);

export const participantScanLogsTable = pgTable("participantScanLogs", {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  createdAt: timestamp("createdAt", { mode: "date" }).notNull().defaultNow(),
  updatedAt: timestamp("updatedAt", { mode: "date" })
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
  identifier: varchar().notNull(),
  firstName: varchar().notNull(),
  lastName: varchar().notNull(),
  email: varchar().notNull(),
  phone: varchar(),
  speciality: varchar(),
  school: varchar(),
  city: varchar(),
  workplace: varchar(),
  gdprConsent: boolean(),
  marketingConsent: boolean(),
  comment: varchar(),
  companyId: integer().references(() => companiesTable.id, {
    onDelete: "cascade",
  }),
  attendantId: integer().references(() => attendantsTable.id, {
    onDelete: "cascade",
  }),
  participantId: integer()
    .notNull()
    .references(() => participantsTable.id, { onDelete: "cascade" }),
});
export type ParticipantScanLog =
  typeof participantScanLogsTable.$inferInsert & {
    id: number;
  };
export const participantScanLogEntitySchema = createSelectSchema(
  participantScanLogsTable,
);

export const companiesToTagsTable = pgTable("companiesToTags", {
  companyId: integer()
    .notNull()
    .references(() => companiesTable.id),
  tagId: integer()
    .notNull()
    .references(() => tagsTable.id),
});

export const sessionsToTagsTable = pgTable("sessionsToTags", {
  sessionId: integer()
    .notNull()
    .references(() => sessionsTable.id, { onDelete: "cascade" }),
  tagId: integer()
    .notNull()
    .references(() => tagsTable.id, { onDelete: "cascade" }),
});

export const sessionsToSpeakersTable = pgTable("sessionsToSpeakers", {
  sessionId: integer()
    .notNull()
    .references(() => sessionsTable.id),
  speakerId: integer()
    .notNull()
    .references(() => speakersTable.id, { onDelete: "cascade" }),
});

export const lecturesToSpeakersTable = pgTable("lecturesToSpeakers", {
  lectureId: integer()
    .notNull()
    .references(() => lecturesTable.id),
  speakerId: integer()
    .notNull()
    .references(() => speakersTable.id, { onDelete: "cascade" }),
});

export const lecturesToSpeakersSchema = createSelectSchema(
  lecturesToSpeakersTable,
);

export const lecturesToTagsTable = pgTable("lecturesToTags", {
  lectureId: integer()
    .notNull()
    .references(() => lecturesTable.id, { onDelete: "cascade" }),
  tagId: integer()
    .notNull()
    .references(() => tagsTable.id, { onDelete: "cascade" }),
});
export const lecturesToTagsSchema = createSelectSchema(lecturesToTagsTable);

export const companiesRelations = relations(companiesTable, ({ many }) => ({
  categories: many(companiesToTagsTable),
  contests: many(contestsTable),
  scanLogs: many(participantScanLogsTable),
}));

export const participantsRelations = relations(
  participantsTable,
  ({ many, one }) => ({
    scanLogs: many(participantScanLogsTable),
    pass: one(participantPassesTable, {
      fields: [participantsTable.identifier],
      references: [participantPassesTable.identifier],
    }),
  }),
);

export const participantPassesRelations = relations(
  participantPassesTable,
  ({ one }) => ({
    participant: one(participantsTable, {
      fields: [participantPassesTable.identifier],
      references: [participantsTable.identifier],
    }),
  }),
);

export const participantScanLogsRelations = relations(
  participantScanLogsTable,
  ({ one }) => ({
    company: one(companiesTable, {
      fields: [participantScanLogsTable.companyId],
      references: [companiesTable.id],
    }),
    attendant: one(attendantsTable, {
      fields: [participantScanLogsTable.attendantId],
      references: [attendantsTable.id],
    }),
    participant: one(participantsTable, {
      fields: [participantScanLogsTable.participantId],
      references: [participantsTable.id],
    }),
  }),
);

export const attendantsRelations = relations(attendantsTable, ({ many }) => ({
  scanLogs: many(participantScanLogsTable),
}));

export const contestsRelations = relations(contestsTable, ({ one }) => ({
  company: one(companiesTable, {
    fields: [contestsTable.companyId],
    references: [companiesTable.id],
  }),
}));

export const companiesToTagsRelations = relations(
  companiesToTagsTable,
  ({ one }) => ({
    company: one(companiesTable, {
      fields: [companiesToTagsTable.companyId],
      references: [companiesTable.id],
    }),
    tag: one(tagsTable, {
      fields: [companiesToTagsTable.tagId],
      references: [tagsTable.id],
    }),
  }),
);

export const tagsRelations = relations(tagsTable, ({ many }) => ({
  companies: many(companiesToTagsTable),
  lectures: many(lecturesToTagsTable),
  sessions: many(sessionsToTagsTable),
}));

export const lecturesToTagsRelations = relations(
  lecturesToTagsTable,
  ({ one }) => ({
    lecture: one(lecturesTable, {
      fields: [lecturesToTagsTable.lectureId],
      references: [lecturesTable.id],
    }),
    tag: one(tagsTable, {
      fields: [lecturesToTagsTable.tagId],
      references: [tagsTable.id],
    }),
  }),
);

export const daysRelations = relations(daysTable, ({ many }) => ({
  lectures: many(lecturesTable),
}));

export const roomsRelations = relations(roomsTable, ({ many }) => ({
  lectures: many(lecturesTable),
}));

export const sessionsRelations = relations(sessionsTable, ({ many }) => ({
  lectures: many(lecturesTable),
  moderators: many(sessionsToSpeakersTable),
  categories: many(sessionsToTagsTable),
}));

export const sessionsToTagsRelations = relations(
  sessionsToTagsTable,
  ({ one }) => ({
    session: one(sessionsTable, {
      fields: [sessionsToTagsTable.sessionId],
      references: [sessionsTable.id],
    }),
    tag: one(tagsTable, {
      fields: [sessionsToTagsTable.tagId],
      references: [tagsTable.id],
    }),
  }),
);

export const sessionsToSpeakersRelations = relations(
  sessionsToSpeakersTable,
  ({ one }) => ({
    session: one(sessionsTable, {
      fields: [sessionsToSpeakersTable.sessionId],
      references: [sessionsTable.id],
    }),
    speaker: one(speakersTable, {
      fields: [sessionsToSpeakersTable.speakerId],
      references: [speakersTable.id],
    }),
  }),
);

export const speakersRelations = relations(speakersTable, ({ many }) => ({
  lectures: many(lecturesToSpeakersTable),
  sessions: many(sessionsToSpeakersTable),
}));

export const lecturesToSpeakersRelations = relations(
  lecturesToSpeakersTable,
  ({ one }) => ({
    lecture: one(lecturesTable, {
      fields: [lecturesToSpeakersTable.lectureId],
      references: [lecturesTable.id],
    }),
    speaker: one(speakersTable, {
      fields: [lecturesToSpeakersTable.speakerId],
      references: [speakersTable.id],
    }),
  }),
);

export const lecturesRelations = relations(lecturesTable, ({ one, many }) => ({
  day: one(daysTable, {
    fields: [lecturesTable.dayDate],
    references: [daysTable.date],
  }),
  room: one(roomsTable, {
    fields: [lecturesTable.roomId],
    references: [roomsTable.id],
  }),
  session: one(sessionsTable, {
    fields: [lecturesTable.sessionId],
    references: [sessionsTable.id],
  }),
  speakers: many(lecturesToSpeakersTable),
  categories: many(lecturesToTagsTable),
}));
